import type { DateFormatPresetId } from '@/helpers/dates';
import { defaultDateFormatPresetId } from '@/helpers/dates';
import type { Org, User } from '@/types';
import { SIDEBAR_DEFAULT_SIZE } from '@components/Sidebar/constants';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type State = {
  isOrgSelectorOpen: boolean;
  isSidebarCollapsed: boolean;
  isSidebarMenuOpen: boolean;
  /** Runtime-only: full org object, rehydrated from selectedOrgId after login. Never persisted. */
  selectedOrganization: Org | undefined;
  /** Persisted: minimal org stub { id: selectedOrgId } or full org. Used to restore org context
   *  during beforeLoad guards before full org is fetched. Enables org-aware redirects on cold start.
   *  NOTE: Single shared key (not per-user scoped). Last selected org is restored on
   *  next login regardless of which user signs in — intentional for single-user-per-browser
   *  deployments. If multi-user browser sharing is needed, restore per-user key scoping.
   */
  selectedOrgStub: { id: string } | undefined;
  /** Persisted: org UUID only — no PII. Deprecated in favor of selectedOrgStub.id; kept for legacy. */
  selectedOrgId: string | undefined;
  user: User | undefined;
  dateFormatPresetId: DateFormatPresetId;
  sidebarSize: number;
  shouldRefreshInstrumentRuns: boolean;
  setShouldRefreshInstrumentRuns: (val: boolean) => void;
};

type Actions = {
  setIsOrgSelectorOpen: (isOpen: boolean) => void;
  setIsSidebarCollapsed: (isCollapsed: boolean) => void;
  setIsSidebarMenuOpen: (isOpen: boolean) => void;
  setSelectedOrganization: (org: Org | undefined) => void;
  setSelectedOrgStub: (stub: { id: string } | undefined) => void;
  setSelectedOrgId: (id: string | undefined) => void;
  setUser: (user: User | undefined) => void;
  setDateFormatPresetId: (dateFormatPresetId: DateFormatPresetId) => void;
  setSidebarSize: (size: number) => void;
};

const useAppStore = create<State & Actions>()(
  devtools(
    persist(
      immer(set => ({
        isOrgSelectorOpen: false,
        isSidebarCollapsed: false,
        isSidebarMenuOpen: false,
        selectedOrganization: undefined,
        selectedOrgStub: undefined,
        selectedOrgId: undefined,
        user: undefined,
        dateFormatPresetId: defaultDateFormatPresetId,
        sidebarSize: SIDEBAR_DEFAULT_SIZE,
        setIsOrgSelectorOpen: (isOpen: boolean) =>
          set(state => {
            state.isOrgSelectorOpen = isOpen;
          }),
        setIsSidebarCollapsed: (isCollapsed: boolean) =>
          set(state => {
            state.isSidebarCollapsed = isCollapsed;
          }),
        setIsSidebarMenuOpen: (isOpen: boolean) =>
          set(state => {
            state.isSidebarMenuOpen = isOpen;
          }),
        setSelectedOrganization: (org: Org | undefined) =>
          set(state => {
            state.selectedOrganization = org;
            // Also update selectedOrgStub when full org is set
            if (org) {
              state.selectedOrgStub = { id: org.id };
            }
          }),
        setSelectedOrgStub: (stub: { id: string } | undefined) =>
          set(state => {
            state.selectedOrgStub = stub;
          }),
        setSelectedOrgId: (id: string | undefined) =>
          set(state => {
            state.selectedOrgId = id;
            // Also update selectedOrgStub when selectedOrgId is set
            if (id) {
              state.selectedOrgStub = { id };
            } else {
              state.selectedOrgStub = undefined;
            }
          }),
        setUser: (user: User | undefined) =>
          set(state => {
            state.user = user;
          }),
        setDateFormatPresetId: (dateFormatPresetId: DateFormatPresetId) =>
          set(state => {
            state.dateFormatPresetId = dateFormatPresetId;
          }),
        setSidebarSize: (size: number) =>
          set(state => {
            state.sidebarSize = size;
          }),
        shouldRefreshInstrumentRuns: false,
        setShouldRefreshInstrumentRuns: (val: boolean) =>
          set(state => {
            state.shouldRefreshInstrumentRuns = val;
          }),
      })),
      {
        name: 'app',
        partialize: state =>
          Object.fromEntries(
            Object.entries(state).filter(([key]) =>
              [
                'isSidebarCollapsed',
                'isSidebarMenuOpen',
                'selectedOrgStub',
                'selectedOrgId',
                'dateFormatPresetId',
                'sidebarSize',
              ].includes(key),
            ),
          ),
      },
    ),
  ),
);

export { useAppStore };
